import { z } from "zod";
import { availabilityStatusSchema, signupStatusSchema } from "./statusSchemas";

// Registration event sub-schema (mirrors single-session schema)
// KB-2673 — `whenIso` / `zoneLabel` carried through unchanged; zod strips unknown
// keys, so omitting them here destroys the provider's stated deadline on a bulk
// edit exactly as it did on a single-session save. Kept in step with
// `offerFormSchema.ts`'s copy.
const registrationEventSchema = z.object({
  eventKey: z.string(),
  when: z.date().optional(),
  wasTimeProvided: z.boolean().default(false),
  note: z.string().default(""),
  whenIso: z.string().optional(),
  zoneLabel: z.string().optional()
});

// Per-row extras schema for date-list mode. This is used in both
// single-object and array-of-objects variants so we define it once.
const dateListExtrasRowSchema = z.object({
  // NEW: Optional per-row location target (campLocations doc ID). When present,
  // the row is created under that location instead of the currently viewed location.
  locationId: z.string().max(120).optional(),
  title: z.string().optional(),
  titleSuffix: z.string().optional(),
  url: z.string().optional(),
  regUrl: z.string().optional(),
  description: z.string().max(2000).optional(),
  // NEW: Optional row-level prefix that will be prepended to the final
  // description for that row. If <description> is also present on the row,
  // the prefix is applied to the row description; otherwise it is applied
  // to the existing Program/form description.
  descriptionPrefix: z.string().max(2000).optional(),
  // NEW: Per-row program selector (Variation Internal ID / key)
  offeringId: z.string().max(80).optional(),
  // NEW: Optional per-row eligibility notes override
  eligibilityNotes: z.string().max(2000).optional(),
  // NEW: Optional per-row override footnotes (program-level defaults exist)
  // NOTE: Bulk Add Sessions importer treats the sentinel "<progDefault>" as "do not override".
  priceNote: z.string().max(2000).optional(),
  timingNote: z.string().max(2000).optional(),
  // NEW: Optional per-row age overrides (years). Only applied server-side if the Program allows session age overrides.
  minAgeInYearsOverride: z.number().min(0).nullable().optional(),
  maxAgeInYearsOverride: z.number().min(0).nullable().optional(),
  // NEW: Optional per-row age overrides (grade keys like 'g_rk', 'g_1'). Only applied server-side if the Program allows session age overrides.
  // Uses existing business rules server-side to populate grade values and effective ages.
  minAgeGradeKeyOverride: z.string().trim().min(1).optional(),
  maxAgeGradeKeyOverride: z.string().trim().min(1).optional(),
  endDate: z.date().optional(),
  // NEW: Optional per-row overrides for statuses (must be valid IDs if present)
  availabilityStatus: availabilityStatusSchema.optional(),
  signupStatus: signupStatusSchema.optional(),
  // NEW: Optional per-row registration events
  // KB-2673 — the two scraper-owned fields are optional here too. This path
  // creates NEW sessions from an operator's date list, so there is normally no
  // stored provenance to preserve; they are accepted so that a row copied from a
  // scraped session is not silently stripped by the parse.
  regEvents: z.array(z.object({
    eventKey: z.string(),
    when: z.date().optional(),
    wasTimeProvided: z.boolean(),
    note: z.string(),
    whenIso: z.string().optional(),
    zoneLabel: z.string().optional()
  })).optional(),
  // NEW: Admin-only per-row session length override
  overrideNumDays: z.number().int().min(1).optional(),
  // NEW: Admin-only per-row price override
  priceOverride: z.number().optional(),
  // NEW: Admin-only per-row price override for derived single-day sessions only
  priceSingleDayOverride: z.number().optional(),
  // Optional per-row tier-2/tier-3 prices (RowFormat <priceTier2>/<priceTier3>); set session-level offers/<id>.pricingTierPriceOverrides.tier2/.tier3
  priceRegularTier2Override: z.number().min(0).nullable().optional(),
  priceRegularTier3Override: z.number().min(0).nullable().optional()
});

export const bulkAddSessionsSchema = z.object({
  // NOTE: Program/title are conditionally required:
  // - If RowFormat includes <offeringId> and <title> for each row, these top-level fields can be blank.
  // - Otherwise they are required, and the UI enforces that at submit-time.
  variationKey: z.string().optional().default(""),
  displayTitle: z.string().optional().default(""),
  description: z.string().max(2000).optional(),
  eligibilityNotes: z.string().optional(),
  url: z.string().optional(),
  regUrl: z.string().optional(),
  recurrencePattern: z.enum(["weekdays", "monday", "tuesday", "wednesday", "thursday", "friday", "saturday", "sunday"]),
  // In range mode these will be present; in list mode they can be omitted
  startDate: z.date().optional(),
  endDate: z.date().optional(),
  availabilityStatus: availabilityStatusSchema,
  signupStatus: signupStatusSchema,
  numDaysOverride: z.number().nullable().optional(),
  myOfferIncrementKey: z.string().nullable().optional(),
  createSingleDaysForOverlaps: z.boolean().optional().default(false),
  // NEW: Optional suffix for titles of generated single-day sessions
  singleDayTitleSuffix: z.string().optional(),

  // NEW: Registration events (optional)
  regEvents: z.array(registrationEventSchema).optional()
    .transform((events) => {
      if (!events) return undefined;
      const validEvents = events.filter(e => e.when !== undefined && e.when !== null);
      return validEvents.length > 0 ? validEvents : undefined;
    })
    .refine((events) => {
      if (!events || events.length === 0) return true;
      const uniqueKeys = ["e_posted", "e_open", "e_closes"];
      for (const key of uniqueKeys) {
        const count = events.filter(e => e.eventKey === key).length;
        if (count > 1) return false;
      }
      return true;
    }, { message: "Each of e_posted, e_open, and e_closes can appear at most once" }),

  // NEW: Optional extras for date-list mode: keyed by epoch day (ms) as string
  //
  // IMPORTANT: We intentionally allow *either* a single extras object OR an
  // array of extras objects for any given date key:
  // - No RowFormat or one RowFormat row per date -> single object
  // - Multiple RowFormat rows sharing the same startDate -> array of objects,
  //   ordered by the rows as entered by the user.
  dateListExtras: z
    .record(z.union([dateListExtrasRowSchema, z.array(dateListExtrasRowSchema)]))
    .optional()
});

export type BulkAddSessionsFormValues = z.infer<typeof bulkAddSessionsSchema>;
